import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FaClock, FaEnvelopeOpenText, FaExclamationCircle, FaRegCalendarCheck, FaSearch } from 'react-icons/fa';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';

import { apiService } from '../../components/Config/api.service';
import HeaderCard from '../../components/Header/HeaderCard';
import { useATraiter } from '../../components/Navigation/ATraiter';
import {
  dateLisible, decalerJours, dureeDuJour, formaterDuree, formaterEcart, jourLisible,
} from '../Espace/formatFeuilleTemps';
import { dateHeureLocale } from '../Presence/formatPointages';

/**
 * Feuilles de temps, côté RH (contrat C22, Yros/Kavero#207, #454).
 *
 * La RH ne ressaisit rien : les salariés préremplissent leur semaine depuis
 * leurs pointages, la corrigent avec un motif et l'envoient. L'écran ne montre
 * pas toutes les semaines de tout le monde : l'accueil dit quoi traiter — un
 * bandeau de chiffres qui sert de filtres rapides, puis les seuls
 * collaborateurs qui ont une semaine à revoir, une ligne chacun —, et une
 * recherche par nom retrouve les autres. La fiche d'un collaborateur montre
 * une semaine à la fois, en détail : la RH la renvoie avec un motif et décide
 * les heures supplémentaires qu'elle porte par les routes de décision des
 * demandes, inchangées.
 *
 * Une seule lecture de la liste d'Odoo (la semaine courante et les trois
 * précédentes, tous les salariés actifs) suffit à l'accueil : chiffres,
 * regroupement, recherche et filtres se calculent dessus. L'état affiché vient
 * toujours d'Odoo : après chaque action, la semaine et la liste se relisent.
 * Son refus s'affiche mot pour mot.
 */

const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-primary)]';
const CLASSE_CHAMP = 'mt-1 block min-h-[44px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-[16px] text-slate-900 focus:border-[color:var(--color-primary)] focus:outline focus:outline-2 focus:outline-[color:var(--color-primary)]';
const CLASSE_BOUTON = `inline-flex min-h-[44px] items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 transition-colors duration-150 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none ${FOCUS}`;
const BOUTON_PRINCIPAL = `inline-flex min-h-[44px] items-center justify-center rounded-lg bg-[--color-primary] px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-[--bg-hover] disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none ${FOCUS}`;
const BOUTON_APPROUVER = `inline-flex min-h-[44px] items-center justify-center rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none ${FOCUS}`;
const BOUTON_REFUSER = `inline-flex min-h-[44px] items-center justify-center rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition-colors duration-150 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none ${FOCUS}`;
const BOUTON_CONFIRMER_REFUS = `inline-flex min-h-[44px] items-center justify-center rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none ${FOCUS}`;
const BOUTON_FLECHE = `flex h-11 w-11 items-center justify-center rounded-lg text-slate-700 transition-colors duration-150 hover:bg-slate-100 disabled:opacity-40 motion-reduce:transition-none ${FOCUS}`;
const LIEN = `inline-flex min-h-[44px] items-center rounded-lg px-2 text-sm font-medium text-[--color-primary] underline-offset-4 hover:underline ${FOCUS}`;
const CLASSE_ERREUR = 'mt-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800';

// Pastille de statut : un point de couleur avant le texte, pour que l'état se
// lise sans dépendre du seul fond.
const STATUT = {
  brouillon: { pastille: 'bg-slate-100 text-slate-700', point: 'bg-slate-400' },
  soumise: { pastille: 'bg-emerald-50 text-emerald-800', point: 'bg-emerald-500' },
  renvoyee: { pastille: 'bg-amber-50 text-amber-800', point: 'bg-amber-500' },
};

const ETAT_CLASSE = {
  en_attente: 'bg-amber-50 text-amber-800',
  validee: 'bg-emerald-50 text-emerald-800',
  refusee: 'bg-red-50 text-red-700',
};

// Une couleur par salarié, stable d'un chargement à l'autre.
const COULEURS_AVATAR = [
  'bg-sky-100 text-sky-700', 'bg-violet-100 text-violet-700', 'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700', 'bg-rose-100 text-rose-700', 'bg-indigo-100 text-indigo-700',
];

const initiales = (nom) => String(nom || '')
  .split(/\s+/).filter(Boolean).slice(0, 2).map((mot) => mot[0].toUpperCase()).join('');

// Ce que la RH doit regarder : une semaine renvoyée, des heures sup à décider,
// un jour corrigé ou un écart (contrat C22, tri des exceptions).
const estException = (ligne) => ligne.etat === 'renvoyee'
  || ligne.heures_sup_en_attente > 0 || ligne.jours_corriges > 0 || ligne.ecart !== 0;

const interpreterProbleme = (erreur, t) => {
  const enveloppe = erreur?.response?.status === 409 ? erreur.response.data : null;
  return enveloppe?.message || t('feuillesTemps.erreurTechnique');
};

// Odoo n'accepte qu'un lundi (`lundi_invalide`) : un jour se ramène au lundi
// de sa semaine.
const lundiDe = (date) => {
  const jourDeSemaine = new Date(`${date}T00:00:00Z`).getUTCDay();
  return decalerJours(date, -((jourDeSemaine + 6) % 7));
};

const aujourdhuiLocal = () => {
  const maintenant = new Date();
  const deux = (nombre) => String(nombre).padStart(2, '0');
  return `${maintenant.getFullYear()}-${deux(maintenant.getMonth() + 1)}-${deux(maintenant.getDate())}`;
};

const normaliser = (texte) => String(texte || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// Les filtres rapides, et leur trace dans l'adresse : celle du lien « À
// traiter » (`?avec_heures_sup=1`) et celles qu'avaient les filtres de statut.
const FILTRES = {
  'heures-sup': { garde: (collaborateur) => collaborateur.heuresSup > 0, parametre: ['avec_heures_sup', '1'], titre: 'heuresSup' },
  renvoyees: { garde: (collaborateur) => collaborateur.renvoyees > 0, parametre: ['etat', 'renvoyee'], titre: 'renvoyees' },
  'non-soumises': { garde: (collaborateur) => collaborateur.nonSoumises > 0, parametre: ['etat', 'brouillon'], titre: 'nonSoumises' },
};

const filtreDeLAdresse = (recherche) => Object.keys(FILTRES)
  .find((cle) => recherche.get(FILTRES[cle].parametre[0]) === FILTRES[cle].parametre[1]) || null;

// Une ligne par salarié et par semaine devient un collaborateur et ses
// semaines. L'ordre d'Odoo (exceptions d'abord, puis le nom) fixe celui des
// collaborateurs.
const regrouper = (lignes) => {
  const parId = new Map();
  lignes.forEach((ligne) => {
    if (!parId.has(ligne.employe.id)) parId.set(ligne.employe.id, { employe: ligne.employe, lignes: [] });
    parId.get(ligne.employe.id).lignes.push(ligne);
  });
  return [...parId.values()].map((collaborateur) => ({
    ...collaborateur,
    aRevoir: collaborateur.lignes.filter(estException).length,
    heuresSup: collaborateur.lignes.reduce((somme, ligne) => somme + (ligne.heures_sup_en_attente || 0), 0),
    renvoyees: collaborateur.lignes.filter((ligne) => ligne.etat === 'renvoyee').length,
    nonSoumises: collaborateur.lignes.filter((ligne) => ligne.etat === 'brouillon').length,
  }));
};

// La fiche s'ouvre sur la semaine la plus récente à revoir, sinon sur la
// semaine en cours.
const semaineParDefaut = (collaborateur, semaineEnCours) => {
  const aRevoir = collaborateur.lignes.filter(estException).map((ligne) => ligne.lundi).sort();
  return aRevoir.length ? aRevoir[aRevoir.length - 1] : semaineEnCours;
};

// Une semaine venue de l'adresse ne s'ouvre que si c'est un lundi, au plus
// la semaine en cours (Odoo refuse une semaine future).
const lundiAccepte = (lundi, semaineEnCours) => Boolean(lundi)
  && /^\d{4}-\d{2}-\d{2}$/.test(lundi)
  && !Number.isNaN(new Date(`${lundi}T00:00:00Z`).getTime())
  && lundiDe(lundi) === lundi
  && lundi <= semaineEnCours;

const Statut = ({ etat, testid }) => {
  const { t } = useTranslation();
  const style = STATUT[etat] || STATUT.brouillon;
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-medium ${style.pastille}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${style.point}`} aria-hidden="true" />
      {t(`feuillesTemps.statuts.${etat}`)}
    </span>
  );
};

const Avatar = ({ employe, grand = false }) => (
  <span
    data-testid={`feuille-avatar-${employe.id}`}
    aria-hidden="true"
    className={`flex shrink-0 items-center justify-center rounded-full font-semibold ${
      COULEURS_AVATAR[employe.id % COULEURS_AVATAR.length]} ${grand ? 'h-12 w-12 text-base' : 'h-10 w-10 text-sm'}`}
  >
    {initiales(employe.nom)}
  </span>
);

// Ce qui attend un collaborateur, en toutes lettres. Les semaines non
// soumises ne s'affichent que sous leur filtre : presque tout le monde en a.
const Signaux = ({ collaborateur, avecNonSoumises = false }) => {
  const { t } = useTranslation();
  const { employe, aRevoir, heuresSup, renvoyees, nonSoumises } = collaborateur;
  const signaux = [
    ['a-revoir', aRevoir, 'nbSemainesARevoir', 'bg-amber-50 text-amber-900'],
    ['heures-sup', heuresSup, 'nbHeuresSup', 'bg-violet-50 text-violet-800'],
    ['renvoyees', renvoyees, 'nbRenvoyees', 'bg-rose-50 text-rose-800'],
    ['non-soumises', avecNonSoumises ? nonSoumises : 0, 'nbNonSoumises', 'bg-slate-100 text-slate-700'],
  ].filter(([, nombre]) => nombre > 0);
  if (!signaux.length) {
    return <span className="text-sm text-slate-600">{t('feuillesTemps.rienARevoirCourt')}</span>;
  }
  return signaux.map(([cle, nombre, libelle, ton]) => (
    <span
      key={cle}
      data-testid={`collaborateur-${cle}-${employe.id}`}
      className={`rounded-full px-2.5 py-0.5 text-sm font-medium ${ton}`}
    >
      {t(`feuillesTemps.${libelle}`, { count: nombre })}
    </span>
  ));
};

/**
 * Une semaine d'un collaborateur, vue par la RH. `onModifie` prévient l'écran
 * qu'une action a changé ce qu'il montre (statut, heures sup en attente).
 */
function SemaineRh({ employe, lundi, onModifie }) {
  const { t, i18n } = useTranslation();
  const langue = i18n.language;
  const { relire } = useATraiter();

  const [semaine, setSemaine] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [motifRenvoi, setMotifRenvoi] = useState('');
  const [refus, setRefus] = useState(null);
  const [actionEnCours, setActionEnCours] = useState(null);

  const charger = useCallback(async () => {
    try {
      setSemaine(await apiService.lireFeuilleTempsRh(employe.id, lundi));
      setErreur(null);
    } catch (probleme) {
      setErreur(interpreterProbleme(probleme, t));
    }
  }, [employe.id, lundi, t]);

  useEffect(() => { charger(); }, [charger]);

  // Une action réussie relit la semaine, prévient l'écran — et la boîte
  // « À traiter » quand elle a décidé une demande.
  // `cle` nomme l'action en cours : son bouton le dit, les autres se désactivent
  // — une décision ne part pas deux fois sur un double clic.
  const agir = async (cle, action, { decision = false } = {}) => {
    if (actionEnCours) return;
    setActionEnCours(cle);
    setErreur(null);
    try {
      await action();
      if (decision) await relire();
      await charger();
      onModifie();
    } catch (probleme) {
      setErreur(interpreterProbleme(probleme, t));
    } finally {
      setActionEnCours(null);
    }
  };

  const renvoyer = () => agir('renvoyer', async () => {
    await apiService.renvoyerFeuilleTemps({ employe_id: employe.id, lundi, motif: motifRenvoi.trim() });
    setMotifRenvoi('');
  });

  const approuver = (id) => agir(`approuver-${id}`, () => apiService.approuverDemandeRh(id, 'heures_sup'), { decision: true });

  const confirmerRefus = () => {
    const motif = refus.motif.trim();
    if (!motif) return undefined;
    return agir(`refuser-${refus.id}`, async () => {
      await apiService.refuserDemandeRh(refus.id, 'heures_sup', motif);
      setRefus(null);
    }, { decision: true });
  };

  const blocErreur = erreur && (
    <p data-testid="detail-erreur" role="alert" className={CLASSE_ERREUR}>{erreur}</p>
  );

  const habillage = (contenu) => (
    <section
      data-testid="feuille-detail"
      aria-label={t('feuillesTemps.semaineDu', {
        debut: dateLisible(lundi, langue), fin: dateLisible(decalerJours(lundi, 6), langue),
      })}
      className="rounded-2xl border border-slate-200 bg-white shadow-sm"
    >
      {contenu}
    </section>
  );

  if (!semaine) {
    return habillage(
      <div className="px-6 py-5">
        {blocErreur || <p role="status" className="text-sm text-slate-600">{t('feuillesTemps.chargement')}</p>}
      </div>
    );
  }

  const pointagesDuJour = (date) => (semaine.pointages || []).filter((pointage) => pointage.date === date);
  const heuresSup = semaine.heures_sup || [];
  const somme = (cle) => semaine.jours.reduce((total, jour) => total + (jour[cle] || 0), 0);
  const totaux = [
    ['attendu', somme('attendu')], ['pointe', somme('pointe')], ['retenu', somme('retenu')],
  ];

  return habillage(
    <>
      <div className="space-y-6 px-6 py-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-slate-900">{t('feuillesTemps.laSemaine')}</h3>
          <Statut etat={semaine.etat} testid="detail-statut" />
        </div>

        <div className="grid grid-cols-3 gap-3">
          {totaux.map(([cle, valeur]) => (
            <div key={cle} data-testid={`detail-total-${cle}`} className="rounded-xl bg-slate-50 px-4 py-3">
              <span className="block text-sm font-medium uppercase tracking-wide text-slate-600">
                {t(`feuillesTemps.${cle}`)}
              </span>
              <span className="mt-1 block text-xl font-semibold text-slate-900">{dureeDuJour(valeur, t)}</span>
            </div>
          ))}
        </div>

        {semaine.etat === 'renvoyee' && semaine.motif_renvoi && (
          <p
            data-testid="detail-motif-renvoi"
            className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            <span className="font-semibold">{t('feuillesTemps.motifRenvoi')}</span>
            {' '}{semaine.motif_renvoi}
          </p>
        )}

        {blocErreur}

        <ul className="space-y-2">
          {semaine.jours.map((jour) => {
            const pointages = pointagesDuJour(jour.date);
            // Un jour sans heures attendues ni pointage (le week-end) tient sur
            // une ligne : il n'a rien à dire.
            if (!jour.attendu && !jour.pointe && !jour.corrige && pointages.length === 0) {
              return (
                <li
                  key={jour.date}
                  data-testid={`detail-jour-${jour.date}`}
                  className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-2 text-sm text-slate-600"
                >
                  <span className="first-letter:uppercase">{jourLisible(jour.date, langue)}</span>
                  <span>{t('feuillesTemps.nonTravaille')}</span>
                </li>
              );
            }
            return (
              <li
                key={jour.date}
                data-testid={`detail-jour-${jour.date}`}
                className={`rounded-xl border px-4 py-3 ${jour.corrige ? 'border-blue-200 bg-blue-50/50' : 'border-slate-200 bg-white'}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold text-slate-900 first-letter:uppercase">{jourLisible(jour.date, langue)}</span>
                  <span className="flex items-center gap-2">
                    {jour.ecart !== 0 && (
                      <span className={`rounded-full px-2.5 py-0.5 text-sm font-medium ${
                        jour.ecart > 0 ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}
                      >
                        {formaterEcart(jour.ecart, t)}
                      </span>
                    )}
                    {jour.corrige && (
                      <span
                        data-testid={`detail-corrige-${jour.date}`}
                        className="rounded-full bg-blue-100 px-2.5 py-0.5 text-sm font-medium text-blue-800"
                      >
                        {t('feuillesTemps.corrige')}
                      </span>
                    )}
                  </span>
                </div>

                <div data-testid={`detail-pointages-${jour.date}`} className="mt-2 flex flex-wrap items-center gap-2">
                  <FaClock className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" />
                  {pointages.length ? pointages.map(({ entree, sortie }) => (
                    <span key={`${entree}-${sortie}`} className="rounded-md bg-slate-100 px-2 py-0.5 text-sm text-slate-700">
                      {entree}–{sortie || '…'}
                    </span>
                  )) : (
                    <span className="text-sm text-slate-600">{t('feuillesTemps.aucunPointage')}</span>
                  )}
                </div>

                {/* La note du salarié, sous la rangée de pastilles et jamais dedans : elle
                    fait jusqu'à 500 caractères. En lecture seule, c'est sa parole. */}
                {pointages.map((pointage, rang) => {
                  if (!pointage.note) return null;
                  const modifieeLe = dateHeureLocale(pointage.note_modifiee_le, semaine.fuseau, langue);
                  return (
                    <div
                      key={`${pointage.entree}-${pointage.sortie}`}
                      data-testid={`detail-note-${jour.date}-${rang}`}
                      className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    >
                      <p className="whitespace-pre-line break-words">
                        <span className="font-medium text-slate-900">
                          {t('feuillesTemps.noteDuSalarie', { debut: pointage.entree, fin: pointage.sortie || '…' })}
                        </span>
                        {' '}
                        {pointage.note}
                      </p>
                      {modifieeLe && (
                        <p data-testid={`detail-note-modifiee-${jour.date}-${rang}`} className="mt-1 text-slate-600">
                          {t('feuillesTemps.noteModifieeLe', modifieeLe)}
                        </p>
                      )}
                    </div>
                  );
                })}

                <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                  {[['attendu', jour.attendu], ['pointe', jour.pointe], ['retenu', jour.retenu]].map(([cle, valeur]) => (
                    <div key={cle} className="flex items-baseline gap-1.5">
                      <dt className="text-sm text-slate-600">{t(`feuillesTemps.${cle}`)}</dt>
                      <dd className={cle === 'retenu' ? 'font-semibold text-slate-900' : 'text-slate-700'}>
                        {dureeDuJour(valeur, t)}
                      </dd>
                    </div>
                  ))}
                </dl>

                {jour.corrige && jour.motif_correction && (
                  <p className="mt-2 text-sm text-blue-900/80">{jour.motif_correction}</p>
                )}
              </li>
            );
          })}
        </ul>

        <section>
          <h4 className="text-base font-semibold text-slate-900">{t('feuillesTemps.heuresSup')}</h4>
          {heuresSup.length === 0 ? (
            <p className="mt-2 text-sm text-slate-600">{t('feuillesTemps.aucuneHeureSup')}</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {heuresSup.map((declaration) => (
                <li
                  key={declaration.id}
                  data-testid={`detail-hs-${declaration.id}`}
                  className="rounded-xl border border-slate-200 px-4 py-3 text-sm"
                >
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-slate-800">
                      <span className="inline-block font-semibold first-letter:uppercase">{jourLisible(declaration.date, langue)}</span>
                      {' · '}{declaration.heure_debut}–{declaration.heure_fin}
                      {' · '}{formaterDuree(declaration.heures, t)}
                    </span>
                    <span className={`self-start rounded-full px-2.5 py-0.5 text-sm font-medium ${ETAT_CLASSE[declaration.etat] || ''}`}>
                      {t(`feuillesTemps.etats.${declaration.etat}`)}
                    </span>
                  </div>
                  <p className="mt-1 text-slate-600">{declaration.motif}</p>
                  {declaration.motif_refus && (
                    <p className="mt-1 text-red-700">{declaration.motif_refus}</p>
                  )}
                  {declaration.etat === 'en_attente' && refus?.id !== declaration.id && (
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        data-testid={`detail-hs-approuver-${declaration.id}`}
                        onClick={() => approuver(declaration.id)}
                        disabled={Boolean(actionEnCours)}
                        className={BOUTON_APPROUVER}
                      >
                        {actionEnCours === `approuver-${declaration.id}` ? t('feuillesTemps.enCours') : t('feuillesTemps.approuver')}
                      </button>
                      <button
                        type="button"
                        data-testid={`detail-hs-refuser-${declaration.id}`}
                        onClick={() => setRefus({ id: declaration.id, motif: '' })}
                        disabled={Boolean(actionEnCours)}
                        className={BOUTON_REFUSER}
                      >
                        {t('feuillesTemps.refuser')}
                      </button>
                    </div>
                  )}
                  {declaration.etat === 'en_attente' && refus?.id === declaration.id && (
                    <div className="mt-3 rounded-xl bg-slate-50 p-4">
                      <label className="block text-sm font-medium text-slate-700">
                        {t('feuillesTemps.motifRefus')}
                        <input
                          type="text"
                          data-testid={`detail-hs-motif-${declaration.id}`}
                          value={refus.motif}
                          maxLength={500}
                          onChange={(evenement) => setRefus({ id: declaration.id, motif: evenement.target.value })}
                          className={CLASSE_CHAMP}
                        />
                      </label>
                      <div className="mt-3 flex justify-end gap-2">
                        <button
                          type="button"
                          data-testid={`detail-hs-annuler-refus-${declaration.id}`}
                          onClick={() => setRefus(null)}
                          disabled={Boolean(actionEnCours)}
                          className={CLASSE_BOUTON}
                        >
                          {t('feuillesTemps.annuler')}
                        </button>
                        <button
                          type="button"
                          data-testid={`detail-hs-confirmer-refus-${declaration.id}`}
                          onClick={confirmerRefus}
                          disabled={!refus.motif.trim() || Boolean(actionEnCours)}
                          className={BOUTON_CONFIRMER_REFUS}
                        >
                          {actionEnCours === `refuser-${refus.id}` ? t('feuillesTemps.enCours') : t('feuillesTemps.confirmerRefus')}
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {semaine.etat === 'soumise' && (
        <footer className="border-t border-slate-100 px-6 py-4">
          <label className="block text-sm font-medium text-slate-700">
            {t('feuillesTemps.motifDuRenvoi')}
            <input
              type="text"
              data-testid="detail-renvoyer-motif"
              value={motifRenvoi}
              maxLength={500}
              onChange={(evenement) => setMotifRenvoi(evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </label>
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              data-testid="detail-renvoyer"
              onClick={renvoyer}
              disabled={!motifRenvoi.trim() || Boolean(actionEnCours)}
              className={BOUTON_PRINCIPAL}
            >
              {actionEnCours === 'renvoyer' ? t('feuillesTemps.enCours') : t('feuillesTemps.renvoyer')}
            </button>
          </div>
        </footer>
      )}
    </>
  );
}

/**
 * La fiche d'un collaborateur : ce qui l'attend sur les quatre dernières
 * semaines, puis une semaine à la fois, en détail.
 */
function FicheCollaborateur({ collaborateur, lundi, semaineEnCours, onSemaine, onRetour, onModifie }) {
  const { t, i18n } = useTranslation();
  const langue = i18n.language;
  const titre = useRef(null);
  const { employe } = collaborateur;

  // Le focus suit le changement de vue : il va au nom du collaborateur.
  useEffect(() => { titre.current?.focus(); }, [employe.id]);

  return (
    <section data-testid="fiche-collaborateur" className="space-y-5">
      <button type="button" data-testid="fiche-retour" onClick={onRetour} className={LIEN}>
        <HiChevronLeft className="mr-1 h-4 w-4" aria-hidden="true" />
        {t('feuillesTemps.tousLesCollaborateurs')}
      </button>

      <header className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar employe={employe} grand />
          <div className="min-w-0">
            <h2
              ref={titre}
              tabIndex={-1}
              data-testid="fiche-nom"
              className="truncate text-lg font-semibold text-slate-900 focus:outline-none"
            >
              {employe.nom}
            </h2>
            <div className="mt-1 flex flex-wrap gap-2">
              <Signaux collaborateur={collaborateur} />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {lundi !== semaineEnCours && (
            <button
              type="button"
              data-testid="fiche-cette-semaine"
              onClick={() => onSemaine(semaineEnCours)}
              className={LIEN}
            >
              {t('feuilleTemps.cetteSemaine')}
            </button>
          )}
          <div className="flex items-center rounded-xl border border-slate-300 bg-white p-1">
            <button
              type="button"
              data-testid="fiche-precedent"
              onClick={() => onSemaine(decalerJours(lundi, -7))}
              aria-label={t('feuilleTemps.semainePrecedente')}
              className={BOUTON_FLECHE}
            >
              <HiChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            <span
              data-testid="fiche-semaine"
              aria-live="polite"
              className="min-w-[12.5rem] px-2 text-center text-base font-medium text-slate-900"
            >
              {t('feuillesTemps.semaineDu', {
                debut: dateLisible(lundi, langue), fin: dateLisible(decalerJours(lundi, 6), langue),
              })}
            </span>
            <button
              type="button"
              data-testid="fiche-suivant"
              onClick={() => onSemaine(decalerJours(lundi, 7))}
              disabled={lundi >= semaineEnCours}
              aria-label={t('feuilleTemps.semaineSuivante')}
              className={BOUTON_FLECHE}
            >
              <HiChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <SemaineRh key={`${employe.id}-${lundi}`} employe={employe} lundi={lundi} onModifie={onModifie} />
    </section>
  );
}

function FeuillesTemps() {
  const { t } = useTranslation();
  const [recherche, setRecherche] = useSearchParams();
  const idListe = useId();

  const [semaines, setSemaines] = useState(null);
  const [probleme, setProbleme] = useState(null);
  const [texte, setTexte] = useState('');

  const employeId = Number(recherche.get('employe_id')) || null;
  const filtre = filtreDeLAdresse(recherche);

  // La dernière requête lancée fait foi : la réponse d'une requête dépassée
  // est ignorée, succès comme échec.
  const derniereRequete = useRef(0);

  const charger = useCallback(async () => {
    const numero = ++derniereRequete.current;
    try {
      const reponse = await apiService.listerFeuillesTemps({});
      if (numero !== derniereRequete.current) return;
      setSemaines(reponse?.semaines || []);
      setProbleme(null);
    } catch (erreur) {
      if (numero !== derniereRequete.current) return;
      setProbleme(interpreterProbleme(erreur, t));
    }
  }, [t]);

  useEffect(() => { charger(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // En revenant d'une fiche, le focus retourne au collaborateur qu'on a quitté.
  const lignesCollaborateurs = useRef({});
  const champRecherche = useRef(null);
  const derniereFiche = useRef(null);
  useEffect(() => {
    if (employeId) {
      derniereFiche.current = employeId;
    } else if (derniereFiche.current) {
      (lignesCollaborateurs.current[derniereFiche.current] || champRecherche.current)?.focus();
      derniereFiche.current = null;
    }
  }, [employeId]);

  // Ouvrir une fiche ajoute une entrée à l'historique (le retour arrière y
  // ramène) ; changer de semaine ou de filtre la remplace.
  const changerAdresse = (modifier, options) => {
    const suivante = new URLSearchParams(recherche);
    modifier(suivante);
    setRecherche(suivante, options);
  };

  const ouvrirFiche = (id) => changerAdresse((adresse) => {
    adresse.set('employe_id', String(id));
    adresse.delete('lundi');
  });

  const fermerFiche = () => changerAdresse((adresse) => {
    adresse.delete('employe_id');
    adresse.delete('lundi');
  });

  const changerSemaine = (lundi) => changerAdresse((adresse) => adresse.set('lundi', lundi), { replace: true });

  const basculerFiltre = (cle) => changerAdresse((adresse) => {
    Object.values(FILTRES).forEach(({ parametre: [nom] }) => adresse.delete(nom));
    if (cle !== filtre) adresse.set(...FILTRES[cle].parametre);
  }, { replace: true });

  // Tant que la première réponse n'est pas là, rien à montrer : un accueil
  // sur une liste absente se lirait comme « rien à revoir ».
  if (semaines === null && !probleme) {
    return (
      <div data-testid="feuilles-chargement" role="status" aria-busy="true" className="space-y-4">
        <span className="sr-only">{t('feuillesTemps.chargement')}</span>
        <HeaderCard title={t('feuillesTemps.titre')} text={t('feuillesTemps.description')} />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((rang) => (
            <div key={rang} className="h-[4.5rem] animate-pulse rounded-2xl bg-slate-200 motion-reduce:animate-none" />
          ))}
        </div>
        {[0, 1, 2].map((rang) => (
          <div key={rang} className="h-20 animate-pulse rounded-2xl bg-slate-200 motion-reduce:animate-none" />
        ))}
      </div>
    );
  }

  const lignes = semaines || [];
  const collaborateurs = regrouper(lignes);
  const semaineEnCours = lignes.reduce((plusRecent, { lundi }) => (lundi > plusRecent ? lundi : plusRecent), '')
    || lundiDe(aujourdhuiLocal());

  const blocProbleme = probleme && (
    <div
      data-testid="feuilles-probleme"
      role="alert"
      className="flex flex-wrap items-center gap-3 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm font-medium text-red-800"
    >
      <span className="min-w-0 flex-1">{probleme}</span>
      <button type="button" data-testid="feuilles-reessayer" onClick={charger} className={CLASSE_BOUTON}>
        {t('feuillesTemps.reessayer')}
      </button>
    </div>
  );

  if (employeId && semaines) {
    const collaborateur = collaborateurs.find(({ employe }) => employe.id === employeId) || {
      employe: { id: employeId, nom: t('feuillesTemps.collaborateurNumero', { id: employeId }) },
      lignes: [], aRevoir: 0, heuresSup: 0, renvoyees: 0, nonSoumises: 0,
    };
    const demande = recherche.get('lundi');
    const lundi = lundiAccepte(demande, semaineEnCours) ? demande : semaineParDefaut(collaborateur, semaineEnCours);
    return (
      <div data-testid="feuilles-temps" className="space-y-6">
        <HeaderCard title={t('feuillesTemps.titre')} text={t('feuillesTemps.description')} />
        {blocProbleme}
        <FicheCollaborateur
          collaborateur={collaborateur}
          lundi={lundi}
          semaineEnCours={semaineEnCours}
          onSemaine={changerSemaine}
          onRetour={fermerFiche}
          onModifie={charger}
        />
      </div>
    );
  }

  const aRevoir = lignes.filter(estException);
  const total = (cle) => lignes.reduce((somme, ligne) => somme + (ligne[cle] || 0), 0);

  // Le bandeau de chiffres : trois d'entre eux filtrent les collaborateurs
  // d'un clic, le premier n'est qu'un compte — l'accueil montre déjà les
  // collaborateurs à revoir.
  const chiffres = [
    {
      cle: 'a-revoir', libelle: t('feuillesTemps.kpi.aRevoir'), valeur: aRevoir.length,
      Icone: FaExclamationCircle, ton: 'bg-amber-100 text-amber-700',
    },
    {
      cle: 'heures-sup', libelle: t('feuillesTemps.kpi.heuresSup'), valeur: total('heures_sup_en_attente'),
      Icone: FaClock, ton: 'bg-violet-100 text-violet-700', filtrant: true,
    },
    {
      cle: 'renvoyees', libelle: t('feuillesTemps.kpi.renvoyees'),
      valeur: lignes.filter((ligne) => ligne.etat === 'renvoyee').length,
      Icone: FaEnvelopeOpenText, ton: 'bg-rose-100 text-rose-700', filtrant: true,
    },
    {
      cle: 'non-soumises', libelle: t('feuillesTemps.kpi.nonSoumises'),
      valeur: lignes.filter((ligne) => ligne.etat === 'brouillon').length,
      Icone: FaRegCalendarCheck, ton: 'bg-slate-100 text-slate-600', filtrant: true,
    },
  ];

  // Qui s'affiche : sans recherche ni filtre, les seuls collaborateurs à
  // revoir ; un filtre ne garde que ceux qu'il concerne ; une recherche
  // trouve n'importe qui, chaque mot tapé devant se trouver dans le nom.
  const termes = normaliser(texte).split(/\s+/).filter(Boolean);
  let visibles = filtre ? collaborateurs.filter(FILTRES[filtre].garde) : collaborateurs;
  if (termes.length) {
    visibles = visibles.filter(({ employe }) => termes.every((terme) => normaliser(employe.nom).includes(terme)));
  } else if (!filtre) {
    visibles = visibles.filter((collaborateur) => collaborateur.aRevoir > 0);
  }

  let titreListe = t('feuillesTemps.listes.aRevoir');
  if (termes.length) titreListe = t('feuillesTemps.listes.resultats');
  else if (filtre) titreListe = t(`feuillesTemps.listes.${FILTRES[filtre].titre}`);

  let messageVide = null;
  if (!visibles.length) {
    if (!collaborateurs.length) messageVide = t('feuillesTemps.aucunCollaborateur');
    else if (termes.length) messageVide = t('feuillesTemps.aucunResultat', { texte: texte.trim() });
    else if (filtre) messageVide = t('feuillesTemps.aucunPourCeFiltre');
    else messageVide = t('feuillesTemps.rienARevoir');
  }
  // « Rien à revoir » dit déjà de taper un nom : la phrase sur les autres
  // collaborateurs n'accompagne qu'une liste non vide.
  const autres = !termes.length && !filtre && visibles.length ? collaborateurs.length - visibles.length : 0;

  return (
    <div data-testid="feuilles-temps" className="space-y-6">
      <HeaderCard title={t('feuillesTemps.titre')} text={t('feuillesTemps.description')} />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {chiffres.map(({ cle, libelle, valeur, Icone, ton, filtrant }) => {
          const contenu = (
            <>
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${ton}`}>
                <Icone className="h-4 w-4" aria-hidden="true" />
              </span>
              <span className="min-w-0 text-left">
                <span className="block text-2xl font-semibold leading-none text-slate-900">{valeur}</span>
                <span className="mt-1 block text-sm text-slate-600">{libelle}</span>
              </span>
            </>
          );
          const classe = 'flex items-center gap-3 rounded-2xl border bg-white p-4 shadow-sm';
          return filtrant ? (
            <button
              key={cle}
              type="button"
              data-testid={`feuilles-kpi-${cle}`}
              aria-pressed={filtre === cle}
              aria-controls={idListe}
              onClick={() => basculerFiltre(cle)}
              className={`${classe} min-h-[44px] transition-colors duration-150 hover:border-slate-300 motion-reduce:transition-none ${FOCUS} ${
                filtre === cle ? 'border-[--color-primary] ring-1 ring-[--color-primary]' : 'border-slate-200'}`}
            >
              {contenu}
            </button>
          ) : (
            <div key={cle} data-testid={`feuilles-kpi-${cle}`} className={`${classe} border-slate-200`}>
              {contenu}
            </div>
          );
        })}
      </div>

      {blocProbleme}

      {semaines && (
        <section aria-labelledby={`${idListe}-titre`} className="space-y-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:p-5">
          <label className="block text-sm font-medium text-slate-700">
            {t('feuillesTemps.rechercher')}
            <span className="relative mt-1 block">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden="true" />
              <input
                ref={champRecherche}
                type="search"
                data-testid="feuilles-recherche"
                value={texte}
                autoComplete="off"
                placeholder={t('feuillesTemps.rechercherAide')}
                aria-controls={idListe}
                onChange={(evenement) => setTexte(evenement.target.value)}
                className={`${CLASSE_CHAMP} mt-0 pl-10`}
              />
            </span>
          </label>

          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h3 id={`${idListe}-titre`} data-testid="feuilles-liste-titre" className="text-base font-semibold text-slate-900">
              {titreListe}
            </h3>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-sm font-medium text-slate-600" aria-hidden="true">
              {visibles.length}
            </span>
            <span data-testid="feuilles-nb-resultats" aria-live="polite" className="sr-only">
              {t('feuillesTemps.nbCollaborateurs', { count: visibles.length })}
            </span>
          </div>

          <ul id={idListe} data-testid="feuilles-collaborateurs" className="space-y-3">
            {visibles.map((collaborateur) => {
              const { employe } = collaborateur;
              return (
                <li key={employe.id}>
                  <button
                    type="button"
                    ref={(element) => { lignesCollaborateurs.current[employe.id] = element; }}
                    data-testid={`feuilles-collaborateur-${employe.id}`}
                    onClick={() => ouvrirFiche(employe.id)}
                    className={`flex min-h-[44px] w-full items-center gap-3 rounded-2xl border bg-white p-4 text-left shadow-sm transition-shadow duration-150 hover:shadow-md motion-reduce:transition-none ${FOCUS} ${
                      collaborateur.aRevoir > 0 ? 'border-slate-200 border-l-4 border-l-amber-400' : 'border-slate-200'}`}
                  >
                    <Avatar employe={employe} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold text-slate-900">{employe.nom}</span>
                      <span className="mt-1 flex flex-wrap gap-2">
                        <Signaux collaborateur={collaborateur} avecNonSoumises={filtre === 'non-soumises'} />
                      </span>
                    </span>
                    <HiChevronRight className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>

          {messageVide && (
            <p data-testid="feuilles-vide" className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-600">
              {messageVide}
            </p>
          )}

          {autres > 0 && (
            <p data-testid="feuilles-autres" className="text-sm text-slate-600">
              {t('feuillesTemps.autresCollaborateurs', { count: autres })}
            </p>
          )}
        </section>
      )}
    </div>
  );
}

export default FeuillesTemps;
